import { Injectable } from '@angular/core';
import { SlWapiService } from './sl-wapi.service';
import { SLLayer } from '../sl-layer';
import { CancellablePromise } from 'src/app/types/cancellable-promise';
import { LayerAttributesArray, AttributeInfo } from 'src/app/types/@types/sl-v-types';
import { NumberQualifierService } from './qualifiers/number-qualifier.service';
import { NumberSciQualifierService } from './qualifiers/number-sci-qualifier.service';
import { NumberHexQualifierService } from './qualifiers/number-hex-qualifier.service';
import { NumberOctalQualifierService } from './qualifiers/number-octal-qualifier.service';
import { NumberBinaryQualifierService } from './qualifiers/number-binary-qualifier.service';
import { NumberFractionQualifierService } from './qualifiers/number-fraction-qualifier.service';
import { NumberPhoneQualifierService } from './qualifiers/number-phone-qualifier.service';
import { NumberZipcodeQualifierService } from './qualifiers/number-zipcode-qualifier.service';
import { DateQualifierService } from './qualifiers/date-qualifier.service';
import { BooleanQualifierService } from './qualifiers/boolean-qualifier.service';
import { TextQualifierService } from './qualifiers/text-qualifier.service.';
import { LinkQualifierService } from './qualifiers/link-qualifier.service';
import { CastingMode } from 'src/app/components/att-casting-view/att-casting-view.component';
import { CastingFormatType } from 'src/app/components/att-casting-view/formats/casting-formats-sel/casting-formats-sel.component';
import { GeoLocationQualifierService } from './qualifiers/geo-location-qualifier-service';
import { MediaQualifierService } from './qualifiers/MediaQualifier.service';


export type AttTargetSource = 'self' | 'layer';

export type AttStorageType = 'boolean' | 'date' | 'time' | 'text' | 'string' | 'float' | 'int' | 'integer' | 'cg_url' | 'url' | 'geometry' | 'bigint';

export type QSId = 'number-general' | 'number-sci' | 'number-dec' | 'number-hex' | 'number-octal' | 'number-binary' | 'number-fraction' | 'number-geoloc-part' | 'number-phone' | 'number-currency' |
	'number-percentage' | 'number-zipcode' | 'time' | 'date' | 'text' | 'boolean' | 'link' | 'media';

export type QSLUTEntry = {
	name: QSId,
	display: string,
	service: any
}
export type QSLUTRef = {
	see: string;
}
export type QSLUT = {
	[K in QSId]: QSLUTEntry
}
export const QSLookup: QSLUT = {
	'number-general': {
		name: 'number-general', display: 'Number - General', service: NumberQualifierService
	},
	'number-sci': {
		name: 'number-sci', display: 'Number - Scientific', service: NumberSciQualifierService
	},
	'number-dec': {
		name: 'number-dec', display: 'Number - Decimal', service: NumberQualifierService
	},
	'number-hex': {
		name: 'number-hex', display: 'Number - Hexadecimal', service: NumberHexQualifierService
	},
	'number-octal': {
		name: 'number-octal', display: 'Number - Octal', service: NumberOctalQualifierService
	},
	'number-binary': {
		name: 'number-binary', display: 'Number - Binary', service: NumberBinaryQualifierService
	},
	'number-fraction': {
		name: 'number-fraction', display: 'Number - Fraction', service: NumberFractionQualifierService
	},
	'number-geoloc-part': {
		name: 'number-geoloc-part', display: 'Number - Geolocation Part', service: GeoLocationQualifierService
	},
	'number-phone': {
		name: 'number-phone', display: 'Number - Phone Number', service: NumberPhoneQualifierService
	},
	'number-currency': {
		name: 'number-currency', display: 'Number - Currency', service: NumberQualifierService
	},
	'number-percentage': {
		name: 'number-percentage', display: 'Number - Percentage', service: NumberFractionQualifierService
	},
	'number-zipcode': {
		name: 'number-zipcode', display: 'Number - Zip Code', service: NumberZipcodeQualifierService
	},
	date: {
		name: 'date', display: 'Date', service: DateQualifierService
	},
	time: {
		name: 'time', display: 'Time', service: DateQualifierService
	},
	text: {
		name: 'text', display: 'Text', service: TextQualifierService
	},
	boolean: {
		name: 'boolean', display: 'Boolean', service: BooleanQualifierService
	},
	link: {
		name: 'link', display: 'Link', service: LinkQualifierService
	},
	media: {
		name: 'media', display: 'Media', service: MediaQualifierService
	}
}
export type QSConvLUTEntry = {
	options: Array<QSLUTEntry>,
	defaultIndex: number;
}
export type QSConvLUT = {
	[K in AttStorageType]: QSConvLUTEntry | QSLUTRef
}
export const QSConversionLookup: QSConvLUT = {

	boolean: {
		options: [
			QSLookup.boolean,
			QSLookup.text
		], defaultIndex: 0
	},
	date: {
		options: [
			QSLookup.date,
			QSLookup.text
		], defaultIndex: 0
	},
	time: {
		options: [
			QSLookup.time,
			QSLookup.text
		], defaultIndex: 0
	},
	text: {
		options: [
			QSLookup.boolean,
			QSLookup.date,
			QSLookup.time,
			QSLookup.link,
			QSLookup.media,
			QSLookup['number-binary'],
			QSLookup['number-currency'],
			QSLookup['number-dec'],
			QSLookup['number-fraction'],
			QSLookup['number-general'],
			QSLookup['number-geoloc-part'],
			QSLookup['number-hex'],
			QSLookup['number-octal'],
			QSLookup['number-percentage'],
			QSLookup['number-phone'],
			QSLookup['number-sci'],
			QSLookup['number-zipcode'],
			QSLookup.text
		], defaultIndex: 14
	},
	string: { see: 'text' },
	float: {
		options: [
			QSLookup['number-general'],
			QSLookup['number-sci'],
			QSLookup['number-dec'],
			QSLookup['number-currency'],
			QSLookup['number-fraction'],
			QSLookup['number-geoloc-part'],
			QSLookup['number-percentage']
		], defaultIndex: 0
	},
	integer: { see: 'int' },
	int: {
		options: [
			QSLookup['number-general'],
			QSLookup['number-binary'],
			QSLookup['number-octal'],
			QSLookup['number-hex'],
			QSLookup['number-currency'],
			QSLookup['number-geoloc-part'],
			QSLookup['number-phone'],
			QSLookup['number-percentage'],
			QSLookup['number-zipcode']
		], defaultIndex: 0
	},
	cg_url: {
		options: [
			QSLookup.media,
			QSLookup.link,
			QSLookup.text
		],
		defaultIndex: 0
	},
	url: { see: 'cg_url' },
  bigint: {see: 'int'},
  geometry: {
    options:[],
    defaultIndex: -1
  }
}



export type AttDataType = {
	id: string;
	label: string;
	storageType: AttStorageType;
}

export type AttDataTypesLookup = {
	[key: string]: AttDataType;
}


export type AttDataTypes = Array<AttDataType>;

export type NewAttributeInfo = {
	name: string;
	type: AttStorageType
}
export type EffectiveValueType = AttStorageType | 'number';
export type EffectiveType = {
	attType: AttStorageType;
	as: CastingFormatType;
	effValType?: EffectiveValueType;
}

@Injectable({
	providedIn: 'root'
})
export class AttributesService {


	constructor(private wapi: SlWapiService) { }
	public static GetConversionLUT(type: AttStorageType): QSConvLUTEntry {
		const lut: QSConvLUTEntry | QSLUTRef = QSConversionLookup[type];
		if (!!(lut as QSLUTRef).see) {
			return QSConversionLookup[(lut as QSLUTRef).see];
		} else {
			return lut as QSConvLUTEntry;
		}
	}
	public static GetDisplayValue(attribute: AttributeInfo, value: any, forExport: boolean = false): string {
		if ([undefined, null, NaN].indexOf(value) > -1) {
			value = '';
			return value;
		}
		const attName = attribute.name;
		const attValue = value;
		if (!!attribute.casting) {
			if (!!attribute.casting.display) {

				if (forExport === false) {
					return QSLookup[attribute.casting.display.type].service.GetDisplayValue(attribute, 'display', attValue);
				} else {
					return QSLookup[attribute.casting.display.type].service.GetTextExportValue(attribute, 'display', attValue);
				}

			} else if (!!attribute.casting.entry) {
				if (forExport === false) {
					return QSLookup[attribute.casting.entry.type].service.GetDisplayValue(attribute, 'entry', attValue);
				} else {
					return QSLookup[attribute.casting.entry.type].service.GetTextExportValue(attribute, 'entry', attValue);
				}
			}
		}
		if (!!attribute.type) {
			switch (attribute.type) {
				case 'boolean':
					if (attValue === 't') {
						return 'True';
					}
					if (attValue === 'f') {
						return 'False';
					}
					if (attValue === true) {
						return 'True';
					}
					if (attValue === false) {
						return 'False';
					}
					break;
				case 'cg_url':
				case 'url':
					if (forExport === true) {
						return attValue;
					}
					return '<a href="' + attValue + '" target="_blank">' + attValue + '</a>';
				case 'date':
					if (value === '') {
						return '';
					}
					return new Date(value).toJSON().split('T').shift();
				case 'float':
				case 'int':
				case 'string':
				case 'text':
				default:
					return value;

			}
		} else {
			if (value.indexOf('http') > -1) {
				return '<a href="' + value + '" target="_blank">' + value + '</a>';
			}
			return '' + value;
		}
	}

	public static GetEffectiveType(attribute: AttributeInfo): EffectiveType {
		const coreType = attribute.type;
		let altType = null;
		if (!!attribute.casting) {
			if (!!attribute.casting.display) {
				altType = attribute.casting.display.type;
			} else if (!!attribute.casting.entry) {
				altType = attribute.casting.entry.type;
			}
		}
		return AttributesService.SetEffectiveValueType(attribute, { attType: coreType, as: altType });

	}
	public static SetEffectiveValueType(attribute: AttributeInfo, type: EffectiveType): EffectiveType {
		if (type.as === null) {
			switch (type.attType) {
				case 'float':
				case 'int':
				case 'integer':
					type.effValType = 'number';
					break;
				default:
					type.effValType = type.attType;
					break;
			}
			return type;
		}
		switch (type.as) {
			case 'boolean':
				type.effValType = 'boolean'
				break;
			case 'date':
				type.effValType = 'date';
				break;
			case 'link':
			case 'media':
				type.effValType = 'cg_url';
				break;
			case 'number-binary':
			case 'number-currency':
			case 'number-dec':
			case 'number-fraction':
			case 'number-general':
			case 'number-geoloc-part':
			case 'number-hex':
			case 'number-octal':
			case 'number-percentage':
			case 'number-phone':
			case 'number-sci':
			case 'number-zipcode':
				type.effValType = 'number';
				break;
			case 'text':
				type.effValType = 'text';
				break;
			case 'time':
				type.effValType = 'time';
				break;
		}
		return type;
	}
	public static EffectiveTypeSupportsStat(attribute: AttributeInfo, statName: string) {
		const effectiveType = AttributesService.GetEffectiveType(attribute);
		switch (effectiveType.effValType) {
			case 'string':
			case 'text':
				if (['count', 'first', 'last'].indexOf(statName) > -1) {
					return true;
				}
				/*if (!!effectiveType.as) {
					return QSLookup[effectiveType.as].service.SupportsStat(statName);
				}*/
				return false;
			case 'url':
			case 'cg_url':
				return (['count', 'first', 'last'].indexOf(statName) > -1);
			case 'boolean':
			case 'date':
				return (['count', 'first', 'last', 'max', 'min'].indexOf(statName) > -1);
			case 'number':
				return (['avg', 'count', 'first', 'last', 'max', 'median', 'min', 'sum', 'stddev'].indexOf(statName) > -1);
		}

	}
	public static GetValue(attribute: AttributeInfo, value: any): any {
		if (!!attribute.casting) {
			if (!!attribute.casting.entry) {
				switch (attribute.type) {
					case 'text':
					case 'string':
						return QSLookup[attribute.casting.entry.type].service.GetNativeValue(attribute, 'entry', value);
					default:
						return value;
				}
			}
		}

		switch (attribute.type) {
			case 'boolean':
				if (value === 't') {
					return true;
				}
				if (value === 'f') {
					return false;
				}

				break;
			case 'date':
				if (value === '') {
					return '';
				}
				const json = new Date(value).toJSON();
				if (json === null) {
					return '';
				}
				return json.split('T').shift();
			case 'cg_url':
			case 'url':
			case 'float':
			case 'int':
			case 'string':
			case 'text':
			default:
				return value;
		}
	}
	public AddAttribute(layer: SLLayer, info: NewAttributeInfo) {
		const request: any = {};
		if (layer.isPLayer === true) {
			request.plid = layer.identifier;
		} else {
			request.lid = layer.id;
		}
		request.name = info.name;
		request.type = info.type;
		return this.wapi.Post('layers/attributes/add/', request);
	}

	public SaveAttributes(layer: SLLayer, attributes: LayerAttributesArray, target: AttTargetSource) {
		const request: any = {};
		if (layer.isPLayer === true) {
			request.plid = layer.identifier;
		} else {
			request.lid = layer.id;
		}
		layer.layerAttributes = attributes;
		request.attributes = JSON.stringify(attributes);
		request.target = target;
		return this.wapi.Post('layers/attributes/upsert/', request);
	}


	public SaveAttribute(layer: SLLayer, attributeName: string, attribute: any, target: AttTargetSource = 'self'): CancellablePromise {
		const request: any = {};
		if (layer.isPLayer === true) {
			request.plid = layer.identifier;
		} else {
			request.lid = layer.id;
		}
		request.att_name = attributeName;
		request.attribute = attribute;
		request.target = target;
		return this.wapi.Post('layers/attributes/set_attribute/', request);
	}
	public GetAttribute(layer: SLLayer, attributeName: string, source: AttTargetSource = 'self'): CancellablePromise {
		const request: any = {};
		if (layer.isPLayer === true) {
			request.plid = layer.identifier;
		} else {
			request.lid = layer.id;
		}
		request.att_name = attributeName;
		request.source = source;
		return this.wapi.Post('layers/attributes/get_attribute/', request);
	}
	public GetAttributes(layer: SLLayer, source: AttTargetSource = 'self'): CancellablePromise {
		const request: any = {};
		if (layer.isPLayer === true) {
			request.plid = layer.identifier;
		} else {
			request.lid = layer.id;
		}
		request.source = source;
		return this.wapi.Post('layers/attributes/list/', request);
	}
	public ClearAttributes(layer: SLLayer, source: AttTargetSource = 'self'): CancellablePromise {
		const request: any = {};
		if (layer.isPLayer === true) {
			request.plid = layer.plid;
		} else {
			request.lid = layer.id;
		}
		request.source = source;
		return this.wapi.Post('layers/attributes/reset/', request);
	}
	public RemoveAttribute(layer: SLLayer, attributeName: string) {
		const request: any = {};
		if (layer.isPLayer === true) {
			request.plid = layer.identifier;
		} else {
			request.lid = layer.id;
		}
		request.name = attributeName;
		return this.wapi.Post('layers/attributes/delete/', request);

	}


}
